import {
  faAlignLeft,
  faCheckSquare,
  faDotCircle,
  faHeading,
  faTasks,
  faTrash,
  faSignature,
} from '@fortawesome/pro-solid-svg-icons'
import React, { useEffect, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import Select, { SingleValue } from 'react-select'

import TextInput from '../../../../../App/Components/Form/TextInput/TextInput'
import { LabelWithIcon } from '../../../../../App/Components/LabelWithIcon'
import { ISurveyQuestion, TQuestionOption } from '../../../Client/ISurvey'
import style from '../cardStyle.scss'

import { ChoiceTypeField } from './ChoiceTypeField/ChoiceTypeField'
import { EditQuestionCardModal } from './EditQuestionCardModal'
import { OpenTypeField } from './OpenTypeField/OpenTypeField'
import { IconWrapper, StyledIcon } from './style'

export type QuestionCardProps = {
  question: ISurveyQuestion
  onFocus: (id: string) => void
  isActive: boolean
  onBlur: (question?: ISurveyQuestion) => void
  onKeyPress: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
}

type Option = {
  value: TQuestionOption
  label: JSX.Element
}

export const EditQuestionCard: React.FC<QuestionCardProps> = props => {
  const { t } = useTranslation()

  const [question, setQuestion] = useState<ISurveyQuestion>(props.question)
  const [modalState, setModalState] = useState<boolean>(false)

  // USE WITH REACT-SELECT
  const options: Option[] = [
    {
      value: 'short-answer',
      label: <LabelWithIcon icon={faHeading} labelText={t('Short Answer')} />,
    },
    {
      value: 'paragraph',
      label: <LabelWithIcon icon={faAlignLeft} labelText={t('Paragraph')} />,
    },
    {
      value: 'radio-buttons',
      label: (
        <LabelWithIcon icon={faDotCircle} labelText={t('Radio Buttons')} />
      ),
    },
    {
      value: 'checkboxes',
      label: <LabelWithIcon icon={faCheckSquare} labelText={t('Checkboxes')} />,
    },
    {
      value: 'condition',
      label: <LabelWithIcon icon={faTasks} labelText={t('Condition')} />,
    },
    {
      value: 'signature',
      label: <LabelWithIcon icon={faSignature} labelText={t('Signature')} />,
    },
  ]

  const getDropdownType = (): Option => {
    if (question.type === 'open') {
      return question.isMultiline ? options[1] : options[0]
    } else if (question.type === 'choice') {
      return question.isMultipleChoice ? options[3] : options[2]
    } else if (question.type === 'condition') {
      return options[4]
    } else {
      return options[5]
    }
  }

  useEffect(() => {
    setQuestion(props.question)
  }, [props.question])

  // SWITCH BETWEEN QUESTION TYPES
  const onUpdateType = (newType: SingleValue<Option>) => {
    const newQuestion = { ...question }
    if (newType === null) {
      return
    }
    switch (newType.value) {
      case 'short-answer':
        newQuestion.type = 'open'
        newQuestion.isMultiline = false
        break
      case 'paragraph':
        newQuestion.type = 'open'
        newQuestion.isMultiline = true
        break
      case 'radio-buttons':
        newQuestion.type = 'choice'
        newQuestion.isMultipleChoice = false
        break
      case 'checkboxes':
        newQuestion.type = 'choice'
        newQuestion.isMultipleChoice = true
        break
      case 'condition':
        newQuestion.type = 'condition'
        newQuestion.isMultiline = true
        break
      case 'signature':
        newQuestion.question = 'Signature'
        newQuestion.type = 'signature'
        newQuestion.isMultipleChoice = false
        break
    }
    props.onBlur(newQuestion)
    setQuestion(newQuestion)
  }

  const DisplayedType = () => {
    if (question.type === 'open') {
      return <OpenTypeField isMultiLine={question.isMultiline} />
    } else if (question.type === 'choice') {
      return (
        <ChoiceTypeField
          isMultipleChoice={question.isMultipleChoice}
          currentChoiceTypeInput={question.choices}
          setChoiceTypeInput={newChoices => {
            const newQuestion = {
              ...question,
              choices: newChoices,
            }
            setQuestion(newQuestion)
            props.onBlur(newQuestion)
          }}
        />
      )
    } else if (question.type === 'signature') {
      return <p>{t('The form will contain a signable field.')}</p>
    }
    return null
  }

  return (
    <Draggable draggableId={props.question.id} index={props.question.order}>
      {(provided: any) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${style.cardContainer}
                        ${props.isActive ? style.cardContainerFocus : null}`}
          id={props.question.id}
          onFocus={() => props.onFocus(props.question.id)}>
          <EditQuestionCardModal
            modalState={modalState}
            setModalState={setModalState}
            passQuestionId={props.question.id}
          />
          <IconWrapper>
            <StyledIcon onClick={() => setModalState(true)} icon={faTrash} />
          </IconWrapper>
          <div className={style.cardHead}>
            <TextInput
              value={question.question}
              onChange={event =>
                setQuestion({
                  ...question,
                  question: event.target.value,
                })
              }
              onBlur={event => {
                const newQuestion = {
                  ...question,
                  question: event.target.value,
                }
                setQuestion(newQuestion)
                props.onBlur(newQuestion)
              }}
              onKeyPress={props.onKeyPress}
              isRequired
              label={t('Question title')}
              formType={question.type}
            />

            <Select
              name="select-type"
              value={getDropdownType()}
              onChange={onUpdateType}
              options={options}
              className={style.cardTypeSelection}
            />
          </div>
          <div className={style.cardBody} key={'fixKeys'}>
            <DisplayedType />
          </div>
        </div>
      )}
    </Draggable>
  )
}
